import { faFile } from '@fortawesome/pro-solid-svg-icons/faFile';
import { faList } from '@fortawesome/pro-solid-svg-icons/faList';
import { faPencil } from '@fortawesome/pro-solid-svg-icons/faPencil';
import { faShare } from '@fortawesome/pro-solid-svg-icons/faShare';

import { useEffect, useState } from 'react';

const urlRe = /\/contacts\/[\w-]+\/reviews(\/[\w-]+)?/;

// Returns an array of page objects for the review page flow
function getPages({ url, documents, review }) {
  let pages = [{
    name: 'Cover Sheet',
    icon: faList,
    nextText: 'Start Reading',
    nextBtnClass: 'btn-success',
    href: url[0],
  }];

  // these page urls only work if there is a reviewId in the URL
  if (url[1]) {
    pages = pages.concat(documents.map((document, i) => ({
      name: document.name,
      icon: faFile,
      nextText: i < (documents.length - 1) ? 'Next Document' : undefined,
      href: `${url[0]}/documents/${document.key}`,
      commentRef: { checklistItemId: document.checklist_item_id },
    })));

    if (review && review.test_scores_enabled) {
      pages.push({
        name: 'Test Scores',
        icon: faShare,
        href: `${url[0]}/test_scores`,
        commentRef: { field: 'test_scores_comment' },
      });
    }

    if (review && review.activities_enabled) {
      pages.push({
        name: 'Additional Activities',
        icon: faShare,
        href: `${url[0]}/activities`,
        commentRef: { field: 'activities_comment' },
      });
    }

    if (pages.length > 1) {
      pages[pages.length - 1].nextText = 'Rate Candidate';
    }

    pages.push({
      name: 'Reader Scores',
      icon: faPencil,
      href: `${url[0]}/summary`,
      commentRef: { field: 'scores_comment' },
    });
  }
  return pages;
}

/**
 * Metadata for a page in the review page flow.
 * @typedef {Object} ReviewPage
 * @property {string} name - page name
 * @property {string} href - page URL
 * @property {Object} icon - fontawesome icon
 * @property {string} nextText - next button text
 * @property {string} nextBtnClass - next button classname
 * @property {Object} commentRef - identifies comment
 */

/**
 * Returns pagination info including metadata about all pages, as well as the current, previous and next pages.
 *
 * @param path the current URL page, e.g. location.pathname
 * @param review the review object
 * @param reviewContact the review contact object from useReviewContact
 * @return {{currPage: {ReviewPage}, pages: ReviewPage[], nextPage: {(null|ReviewPage)}, prevPage: {(null|ReviewPage)}}}
 */
const useReviewPaginator = ({ path, review = {}, reviewContact }) => {
  const [reviewPages, setReviewPages] = useState({ currPage: null, prevPage: null, nextPage: null, pages: [] });
  const documents = reviewContact.contact._documents || [];
  const url = urlRe.exec(path);

  if (!url) {
    throw 'path contains invalid review URL';
  }

  useEffect(() => {
    let pages = getPages({ url, documents, review });

    const i = pages.findIndex(page => page.href === path);

    setReviewPages({
      currPage: i !== -1 ? pages[i] : pages[0],
      prevPage: i > 0 ? pages[i - 1] : null,
      nextPage: i !== -1 ? pages[i + 1] : pages[1],
      pages
    });
  }, [reviewContact.ready, review.id, path]);

  return reviewPages;
};

export default useReviewPaginator;
